import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mic, Calendar, CheckCircle, Trash2, Loader2, User, Link2, Pencil, Building2, Target, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';
import DeleteDialog from '../components/DeleteDialog';
import {
  VoiceNoteHeader,
  VoiceNoteAudioPlayer,
  VoiceNoteCategory,
  VoiceNoteTranscription,
  VoiceNoteProcessing,
  VoiceNoteInfoSidebar,
  VoiceNoteTasksEditor,
  VoiceNoteActivityProgress,
  classificationOptions
} from '../components/voice-note';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Constants
const activityTypes = [
  { value: 'meeting', label: 'Reunión' },
  { value: 'call', label: 'Llamada' },
  { value: 'email', label: 'Email' },
  { value: 'reflexion', label: 'Reflexión' },
  { value: 'evento', label: 'Evento' }
];

const companyCategories = [
  { value: 'typsa', label: 'TYPSA' },
  { value: 'publica', label: 'Pública' },
  { value: 'privada', label: 'Privada' }
];

const typsaOrgCategories = [
  { value: 'division', label: 'División' },
  { value: 'departamento', label: 'Departamento' },
  { value: 'territorio', label: 'Territorio' },
  { value: 'grupo', label: 'Grupo' }
];

const VoiceNoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isEditor, currentUser } = useUser();
  
  // Core data
  const [note, setNote] = useState(null);
  const [company, setCompany] = useState(null);
  const [opportunity, setOpportunity] = useState(null);
  const [typsaUnit, setTypsaUnit] = useState(null);
  const [linkedActivity, setLinkedActivity] = useState(null);
  const [linkedContact, setLinkedContact] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Related data
  const [companies, setCompanies] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [typsaOrgUnits, setTypsaOrgUnits] = useState([]);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  
  // Processing states
  const [transcribing, setTranscribing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [creatingTasks, setCreatingTasks] = useState(false);
  const [editableTasks, setEditableTasks] = useState([]);
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Recategorize dialog
  const [recategorizeDialogOpen, setRecategorizeDialogOpen] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [recategorizing, setRecategorizing] = useState(false);
  
  // Activity linking
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [activityMode, setActivityMode] = useState('new');
  const [selectedActivityId, setSelectedActivityId] = useState('');
  const [availableActivities, setAvailableActivities] = useState([]);
  const [activityTitle, setActivityTitle] = useState('');
  const [activityType, setActivityType] = useState('meeting');
  const [activityDate, setActivityDate] = useState('');
  const [creatingActivity, setCreatingActivity] = useState(false);
  const [companyCategory, setCompanyCategory] = useState('');
  const [typsaOrgCategory, setTypsaOrgCategory] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedTypsaOrgUnitId, setSelectedTypsaOrgUnitId] = useState('');
  const [selectedOpportunityId, setSelectedOpportunityId] = useState('');
  
  // Contact linking (for relationship_history)
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactMode, setContactMode] = useState('existing'); // 'existing' or 'new'
  const [contactCompanyCategory, setContactCompanyCategory] = useState('');
  const [contactTypsaOrgCategory, setContactTypsaOrgCategory] = useState('');
  const [contactSelectedCompanyId, setContactSelectedCompanyId] = useState('');
  const [contactSelectedTypsaOrgUnitId, setContactSelectedTypsaOrgUnitId] = useState('');
  const [selectedContactId, setSelectedContactId] = useState('');
  const [linkingContact, setLinkingContact] = useState(false);
  
  // New contact form data (for creating contact from voice note)
  const [newContactData, setNewContactData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    role: '',
    linkedin: '',
    notes: ''
  });
  const [creatingNewContact, setCreatingNewContact] = useState(false);
  
  // Company linking (for account_history)
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [companyMode, setCompanyMode] = useState('existing'); // 'existing' or 'new'
  const [companyLinkType, setCompanyLinkType] = useState(''); // 'typsa', 'privada', 'publica'
  const [companyLinkTypsaCategory, setCompanyLinkTypsaCategory] = useState(''); // division, departamento, etc.
  const [selectedLinkTypsaUnitId, setSelectedLinkTypsaUnitId] = useState('');
  const [companyFilterCategory, setCompanyFilterCategory] = useState('');
  const [selectedLinkCompanyId, setSelectedLinkCompanyId] = useState('');
  const [linkingCompany, setLinkingCompany] = useState(false);
  const [linkedCompany, setLinkedCompany] = useState(null);
  const [linkedTypsaUnit, setLinkedTypsaUnit] = useState(null);
  
  // New company form data (for creating company from voice note)
  const [newCompanyFormData, setNewCompanyFormData] = useState({
    name: '',
    company_type: 'privada',
    sector: '',
    country: 'España',
    city: '',
    website: '',
    status: 'Prospecto',
    rol: 'Cliente'
  });
  const [creatingNewCompany, setCreatingNewCompany] = useState(false);
  
  // Create company/opportunity in activity dialog
  const [showNewCompanyForm, setShowNewCompanyForm] = useState(false);
  const [showNewOpportunityForm, setShowNewOpportunityForm] = useState(false);
  const [newActivityCompanyData, setNewActivityCompanyData] = useState({ name: '', company_type: 'privada', status: 'Prospecto', rol: 'Cliente' });
  const [newActivityOpportunityData, setNewActivityOpportunityData] = useState({ title: '', stage: 'Detectada', estimated_value: '' });
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({ creator: '', company_id: '', opportunity_id: '', typsa_org_unit_id: '' });
  const [saving, setSaving] = useState(false);
  const [editCompanyType, setEditCompanyType] = useState('');
  const [editTypsaCategory, setEditTypsaCategory] = useState('');
  
  // Create company/opportunity
  const [createCompanyMode, setCreateCompanyMode] = useState(false);
  const [createOpportunityMode, setCreateOpportunityMode] = useState(false);
  const [newCompanyData, setNewCompanyData] = useState({ name: '', company_type: 'privada', sector: '', country: '', city: '', website: '', status: 'Prospecto', rol: '', owner: '' });
  const [newOpportunityData, setNewOpportunityData] = useState({ title: '', description: '', stage: 'Detectada', estimated_value: '' });
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [creatingOpportunity, setCreatingOpportunity] = useState(false);

  // Fetch functions
  const fetchNoteData = useCallback(async () => {
    try {
      const noteRes = await axios.get(`${API}/voice-notes/${id}`);
      setNote(noteRes.data);

      if (noteRes.data.company_id) {
        const companyRes = await axios.get(`${API}/companies/${noteRes.data.company_id}`);
        setCompany(companyRes.data);
        setTypsaUnit(null);
      } else if (noteRes.data.typsa_org_unit_id) {
        const unitRes = await axios.get(`${API}/typsa-org-units/${noteRes.data.typsa_org_unit_id}`);
        setTypsaUnit(unitRes.data);
        setCompany(null);
      } else {
        setCompany(null);
        setTypsaUnit(null);
      }

      if (noteRes.data.opportunity_id) {
        const oppRes = await axios.get(`${API}/opportunities/${noteRes.data.opportunity_id}`);
        setOpportunity(oppRes.data);
      } else {
        setOpportunity(null);
      }

      // Fetch linked activity if exists
      if (noteRes.data.linked_activity_id) {
        try {
          const activityRes = await axios.get(`${API}/activities/${noteRes.data.linked_activity_id}`);
          setLinkedActivity(activityRes.data);
        } catch (err) {
          console.error('Error fetching linked activity:', err);
          setLinkedActivity(null);
        }
      } else {
        setLinkedActivity(null);
      }

      // Fetch linked contact if exists (for relationship_history)
      if (noteRes.data.linked_contact_id) {
        try {
          const contactRes = await axios.get(`${API}/contacts/${noteRes.data.linked_contact_id}`);
          setLinkedContact(contactRes.data);
        } catch (err) {
          console.error('Error fetching linked contact:', err);
          setLinkedContact(null);
        }
      } else {
        setLinkedContact(null);
      }

      // Fetch linked company if exists (for account_history)
      if (noteRes.data.linked_company_id) {
        try {
          const companyRes = await axios.get(`${API}/companies/${noteRes.data.linked_company_id}`);
          setLinkedCompany(companyRes.data);
          setLinkedTypsaUnit(null);
        } catch (err) {
          console.error('Error fetching linked company:', err);
          setLinkedCompany(null);
        }
      } else {
        setLinkedCompany(null);
      }

      // Fetch linked TYPSA unit if exists (for account_history)
      if (noteRes.data.linked_typsa_org_unit_id) {
        try {
          const unitRes = await axios.get(`${API}/typsa-org-units/${noteRes.data.linked_typsa_org_unit_id}`);
          setLinkedTypsaUnit(unitRes.data);
          setLinkedCompany(null);
        } catch (err) {
          console.error('Error fetching linked TYPSA unit:', err);
          setLinkedTypsaUnit(null);
        }
      } else {
        setLinkedTypsaUnit(null);
      }
    } catch (error) {
      console.error('Error fetching voice note:', error);
      toast.error('Error al cargar la nota de voz');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchRelatedData = useCallback(async () => {
    try {
      const [companiesRes, oppsRes, typsaUnitsRes, usersRes, contactsRes] = await Promise.all([
        axios.get(`${API}/companies`),
        axios.get(`${API}/opportunities`),
        axios.get(`${API}/typsa-org-units`),
        axios.get(`${API}/users`),
        axios.get(`${API}/contacts`)
      ]);
      setCompanies(companiesRes.data);
      setOpportunities(oppsRes.data);
      setTypsaOrgUnits(typsaUnitsRes.data);
      setUsers(usersRes.data);
      setContacts(contactsRes.data);
    } catch (error) {
      console.error('Error fetching related data:', error);
    }
  }, []);

  useEffect(() => {
    fetchNoteData();
    fetchRelatedData();
  }, [id, fetchNoteData, fetchRelatedData]);

  useEffect(() => {
    // Feb 2026 fix: only seed editableTasks ONCE per voice-note session (when
    // there is no local editing yet). Previously any refetch of `note` blew
    // away the user's assignee/company/opportunity selections silently — the
    // symptom was: user picks responsable + empresa in VoiceNoteTasksEditor,
    // clicks Crear Tareas, and the resulting tasks land in Tareas without
    // responsable or empresa. Now we only re-seed when the incoming preview
    // has a different length (new tasks appeared/disappeared server-side).
    if (note?.extracted_tasks_preview) {
      setEditableTasks((prev) => {
        // Preserve the user's edits: if we already have the same number of
        // tasks locally, keep the local state (which contains the picks).
        if (prev && prev.length === note.extracted_tasks_preview.length) {
          return prev;
        }
        // Deep-clone so mutating editableTasks never mutates note.
        return note.extracted_tasks_preview.map((t) => ({ ...t }));
      });
    }
    if (note?.suggested_title) setActivityTitle(note.suggested_title);
    // Set activity date from note date_time
    if (note?.date_time) {
      setActivityDate(new Date(note.date_time).toISOString().split('T')[0]);
    }
  }, [note]);

  // Reset activity dialog when opened
  useEffect(() => {
    if (activityDialogOpen) {
      setShowNewCompanyForm(false);
      setShowNewOpportunityForm(false);
      setNewActivityCompanyData({ name: '', company_type: 'privada', status: 'Prospecto', rol: 'Cliente' });
      setNewActivityOpportunityData({ title: '', stage: 'Detectada', estimated_value: '' });
      if (activityMode === 'new') {
        setSelectedCompanyId('');
        setSelectedTypsaOrgUnitId('');
        setSelectedOpportunityId('');
        setCompanyCategory('');
        setTypsaOrgCategory('');
      }
    }
  }, [activityDialogOpen, activityMode]);

  // Helper to check if AI actually processed the note (has real data)
  const hasAIProcessedData = () => {
    const isRelationshipHistory = note?.classified_as === 'relationship_history';
    const isAccountHistory = note?.classified_as === 'account_history';
    
    if (note?.ai_structured_data && Object.keys(note.ai_structured_data).length > 0) {
      if (isRelationshipHistory) {
        return !!note.ai_structured_data.relational_analysis || !!note.ai_structured_data.context_summary;
      }
      if (isAccountHistory) {
        return !!note.ai_structured_data.strategic_analysis || !!note.ai_structured_data.executive_summary;
      }
      return true;
    }
    
    if (isRelationshipHistory) {
      return !!note?.relationship_history_ai_result;
    }
    
    if (isAccountHistory) {
      return !!note?.account_history_ai_result;
    }
    
    return !!note?.structured_summary?.key_points?.length || 
           !!note?.extracted_tasks_preview?.length ||
           !!note?.activity_ai_result;
  };

  // Handlers
  const handleTranscribe = async () => {
    setTranscribing(true);
    try {
      await axios.post(`${API}/voice-notes/${id}/transcribe`);
      toast.success('Transcripción completada');
      await fetchNoteData();
    } catch (error) {
      toast.error('Error al transcribir: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTranscribing(false);
    }
  };

  const handleProcess = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/voice-notes/${id}/process`);
      toast.success('Procesamiento completado');
      await fetchNoteData();
    } catch (error) {
      toast.error('Error al procesar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const handleProcessByCategory = async () => {
    if (!note?.classified_as) return toast.error('Esta nota no tiene categoría asignada');
    setProcessing(true);
    try {
      await axios.post(`${API}/voice-notes/${id}/process-category`, {}, { headers: { 'x-user-email': currentUser?.email } });
      toast.success('Procesamiento por categoría completado');
      await fetchNoteData();
    } catch (error) {
      toast.error('Error al procesar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const handleRecategorize = async () => {
    if (!newCategory) return toast.error('Selecciona una categoría');
    setRecategorizing(true);
    try {
      await axios.put(`${API}/voice-notes/${id}/recategorize`, { new_category: newCategory }, { headers: { 'x-user-email': currentUser?.email } });
      toast.success('Categoría actualizada');
      setRecategorizeDialogOpen(false);
      await axios.post(`${API}/voice-notes/${id}/process-category`, {}, { headers: { 'x-user-email': currentUser?.email } });
      toast.success('Reprocesamiento completado');
      await fetchNoteData();
    } catch (error) {
      toast.error('Error al recategorizar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRecategorizing(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/voice-notes/${id}`);
      toast.success('Nota de voz eliminada');
      navigate('/voice-notes');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleCreateTasks = async () => {
    if (!editableTasks?.length) return toast.error('No hay tareas para crear');
    setCreatingTasks(true);
    try {
      const response = await axios.post(`${API}/voice-notes/${id}/create-tasks`, { tasks: editableTasks }, { headers: { 'X-User-Email': currentUser?.email } });
      toast.success(`${response.data.tasks_created} tarea(s) creada(s)`);
      await fetchNoteData();
    } catch (error) {
      toast.error('Error al crear tareas: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCreatingTasks(false);
    }
  };

  // Edit dialog handlers
  const openEditDialog = () => {
    setEditFormData({ creator: note?.creator || '', company_id: note?.company_id || '', opportunity_id: note?.opportunity_id || '', typsa_org_unit_id: note?.typsa_org_unit_id || '' });
    if (note?.company_id) {
      const existingCompany = companies.find(c => c.id === note.company_id);
      setEditCompanyType(existingCompany?.company_type || 'privada');
    } else if (note?.typsa_org_unit_id) {
      setEditCompanyType('typsa');
      const existingUnit = typsaOrgUnits.find(u => u.id === note.typsa_org_unit_id);
      setEditTypsaCategory(existingUnit?.category || '');
    } else {
      setEditCompanyType('');
      setEditTypsaCategory('');
    }
    setCreateCompanyMode(false);
    setCreateOpportunityMode(false);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/voice-notes/${id}`, editFormData);
      toast.success('Nota de voz actualizada');
      setEditDialogOpen(false);
      await fetchNoteData();
    } catch (error) {
      toast.error('Error al actualizar la nota de voz');
    } finally {
      setSaving(false);
    }
  };

  // Filter helpers
  const getFilteredCompanies = () => (!editCompanyType || editCompanyType === 'typsa') ? [] : companies.filter(c => c.company_type === editCompanyType);
  const getFilteredTypsaUnits = () => {
    if (!editTypsaCategory) return [];
    const normalizedCategory = editTypsaCategory.toLowerCase().trim();
    return typsaOrgUnits.filter(u => 
      u.category && u.category.toLowerCase().trim() === normalizedCategory
    );
  };
  
  // Helper function to filter TYPSA units by category (case-insensitive)
  const filterTypsaUnitsByCategory = (category) => {
    if (!category) return [];
    const normalizedCategory = category.toLowerCase().trim();
    return typsaOrgUnits.filter(u => 
      u.category && u.category.toLowerCase().trim() === normalizedCategory
    );
  };
  
  const getFilteredOpportunities = () => {
    if (editCompanyType === 'typsa' && editFormData.typsa_org_unit_id) return opportunities.filter(o => o.typsa_org_unit_id === editFormData.typsa_org_unit_id);
    if (editFormData.company_id) return opportunities.filter(o => o.company_id === editFormData.company_id);
    return [];
  };

  // Activity linking
  const fetchUndocumentedActivities = async () => {
    try {
      let url = `${API}/voice-notes/undocumented-activities`;
      const params = new URLSearchParams();
      if (companyCategory) params.append('company_type', companyCategory);
      if (companyCategory === 'typsa' && typsaOrgCategory) params.append('typsa_category', typsaOrgCategory);
      if (selectedCompanyId) params.append('company_id', selectedCompanyId);
      if (selectedTypsaOrgUnitId) params.append('typsa_org_unit_id', selectedTypsaOrgUnitId);
      if (params.toString()) url += `?${params.toString()}`;
      const response = await axios.get(url);
      setAvailableActivities(response.data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  useEffect(() => {
    if (activityDialogOpen && activityMode === 'existing') fetchUndocumentedActivities();
  }, [activityDialogOpen, activityMode, companyCategory, typsaOrgCategory, selectedCompanyId, selectedTypsaOrgUnitId]);

  // Get opportunities filtered by selected company
  const getFilteredOpportunitiesForActivity = () => {
    if (companyCategory === 'typsa' && selectedTypsaOrgUnitId) {
      return opportunities.filter(o => o.typsa_org_unit_id === selectedTypsaOrgUnitId);
    }
    if (selectedCompanyId) {
      return opportunities.filter(o => o.company_id === selectedCompanyId);
    }
    return [];
  };

  // Create company in activity dialog
  const handleCreateCompanyInActivity = async () => {
    if (!newActivityCompanyData.name.trim()) return toast.error('El nombre de la empresa es obligatorio');
    setCreatingCompany(true);
    try {
      const payload = {
        ...newActivityCompanyData,
        company_type: companyCategory === 'typsa' ? 'typsa' : newActivityCompanyData.company_type
      };
      const response = await axios.post(`${API}/companies`, payload);
      toast.success('Empresa creada');
      // Refresh companies list
      const companiesRes = await axios.get(`${API}/companies`);
      setCompanies(companiesRes.data);
      setSelectedCompanyId(response.data.id);
      setShowNewCompanyForm(false);
      setNewActivityCompanyData({ name: '', company_type: 'privada', status: 'Prospecto', rol: 'Cliente' });
    } catch (error) {
      toast.error('Error al crear empresa: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCreatingCompany(false);
    }
  };

  // Create opportunity in activity dialog
  const handleCreateOpportunityInActivity = async () => {
    if (!newActivityOpportunityData.title.trim()) return toast.error('El título de la oportunidad es obligatorio');
    setCreatingOpportunity(true);
    try {
      const payload = {
        title: newActivityOpportunityData.title,
        stage: newActivityOpportunityData.stage || 'Detectada',
        estimated_value: newActivityOpportunityData.estimated_value ? parseFloat(newActivityOpportunityData.estimated_value) : null,
        company_id: companyCategory !== 'typsa' ? selectedCompanyId : null,
        typsa_org_unit_id: companyCategory === 'typsa' ? selectedTypsaOrgUnitId : null
      };
      const response = await axios.post(`${API}/opportunities`, payload, { headers: { 'X-User-Email': currentUser?.email } });
      toast.success('Oportunidad creada');
      // Refresh opportunities list
      const oppsRes = await axios.get(`${API}/opportunities`);
      setOpportunities(oppsRes.data);
      setSelectedOpportunityId(response.data.id);
      setShowNewOpportunityForm(false);
      setNewActivityOpportunityData({ title: '', stage: 'Detectada', estimated_value: '' });
    } catch (error) {
      toast.error('Error al crear oportunidad: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCreatingOpportunity(false);
    }
  };

  const handleLinkActivity = async () => {
    if (activityMode === 'existing' && !selectedActivityId) return toast.error('Selecciona una actividad');
    if (activityMode === 'new') {
      if (!activityTitle.trim()) return toast.error('El título es obligatorio');
      if (!activityDate) return toast.error('La fecha es obligatoria');
      // Validate company/opportunity
      if (companyCategory === 'typsa') {
        if (!selectedTypsaOrgUnitId) return toast.error('Selecciona una unidad TYPSA');
      } else if (companyCategory) {
        if (!selectedCompanyId) return toast.error('Selecciona una empresa');
      } else {
        return toast.error('Selecciona un tipo de empresa');
      }
      if (!selectedOpportunityId) return toast.error('Selecciona una oportunidad');
    }
    setCreatingActivity(true);
    try {
      if (activityMode === 'existing') {
        // Extract AI structured data from voice note
        const ss = note?.structured_summary || {};
        
        // Update the existing activity with voice note data
        const activityUpdatePayload = {
          voice_note_id: id,
          transcript: note?.transcript_text || null,
          key_points: ss.key_points || null,
          decisions: ss.decisions || null,
          next_steps: ss.next_steps || null,
          risks_concerns: ss.risks_concerns || null,
          opportunity_signals: ss.opportunity_signals || null,
          // Generate summary from transcript if not exists
          summary: note?.transcript_text ? `Transcripción de nota de voz: ${note.transcript_text.substring(0, 500)}${note.transcript_text.length > 500 ? '...' : ''}` : null
        };
        
        await axios.put(`${API}/activities/${selectedActivityId}`, activityUpdatePayload, { headers: { 'x-user-email': currentUser?.email } });
        
        // Also update the voice note with linked_activity_id
        await axios.put(`${API}/voice-notes/${id}`, { linked_activity_id: selectedActivityId });
        toast.success('Nota vinculada a actividad existente');
      } else {
        // Extract AI structured data if available
        const ss = note?.structured_summary || {};
        
        const payload = { 
          title: activityTitle, 
          type: activityType, 
          date: activityDate,
          company_id: companyCategory !== 'typsa' ? selectedCompanyId : null, 
          typsa_org_unit_id: companyCategory === 'typsa' ? selectedTypsaOrgUnitId : null,
          opportunity_id: selectedOpportunityId,
          // AI-extracted fields
          key_points: ss.key_points || null,
          decisions: ss.decisions || null,
          next_steps: ss.next_steps || null,
          risks_concerns: ss.risks_concerns || null,
          opportunity_signals: ss.opportunity_signals || null,
          transcript: note?.transcript_text || null,
          voice_note_id: id
        };
        const response = await axios.post(`${API}/activities`, payload, { headers: { 'x-user-email': currentUser?.email } });
        // Vincular la nota de voz con la actividad creada
        await axios.put(`${API}/voice-notes/${id}`, { linked_activity_id: response.data.id });
        toast.success('Actividad creada y vinculada');
      }
      setActivityDialogOpen(false);
      await fetchNoteData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCreatingActivity(false);
    }
  };

  // Handle linking voice note to contact (for relationship_history)
  const handleLinkContact = async () => {
    if (!selectedContactId) return toast.error('Selecciona un contacto');
    
    setLinkingContact(true);
    try {
      await axios.post(`${API}/voice-notes/${id}/link-contact?contact_id=${selectedContactId}`, null, {
        headers: { 'x-user-email': currentUser?.email }
      });
      toast.success('Nota de voz vinculada al contacto');
      setContactDialogOpen(false);
      await fetchNoteData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLinkingContact(false);
    }
  };

  // Handle creating a new contact and linking the voice note to it
  const handleCreateAndLinkContact = async () => {
    // Validate required fields
    if (!newContactData.first_name.trim()) return toast.error('El nombre es obligatorio');
    if (!newContactData.last_name.trim()) return toast.error('El apellido es obligatorio');
    if (!newContactData.email.trim()) return toast.error('El email es obligatorio');
    
    // Validate company/typsa selection
    if (contactCompanyCategory === 'typsa') {
      if (!contactSelectedTypsaOrgUnitId) return toast.error('Selecciona una unidad TYPSA');
    } else if (contactCompanyCategory) {
      if (!contactSelectedCompanyId) return toast.error('Selecciona una empresa');
    } else {
      return toast.error('Selecciona un tipo de entidad');
    }
    
    setCreatingNewContact(true);
    try {
      // Create the contact
      const contactPayload = {
        ...newContactData,
        company_id: contactCompanyCategory !== 'typsa' ? contactSelectedCompanyId : null,
        typsa_org_unit_id: contactCompanyCategory === 'typsa' ? contactSelectedTypsaOrgUnitId : null
      };
      
      const contactRes = await axios.post(`${API}/contacts`, contactPayload, {
        headers: { 'x-user-email': currentUser?.email }
      });
      
      const newContactId = contactRes.data.id;
      
      // Link the voice note to the new contact
      await axios.post(`${API}/voice-notes/${id}/link-contact?contact_id=${newContactId}`, null, {
        headers: { 'x-user-email': currentUser?.email }
      });
      
      toast.success(`Contacto "${newContactData.first_name} ${newContactData.last_name}" creado y vinculado`);
      setContactDialogOpen(false);
      
      // Reset form
      setNewContactData({ first_name: '', last_name: '', email: '', phone: '', role: '', linkedin: '', notes: '' });
      setContactMode('existing');
      
      // Refresh data
      await fetchNoteData();
      await fetchRelatedData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCreatingNewContact(false);
    }
  };

  // Filter contacts based on selected company/typsa unit
  const getFilteredContacts = () => {
    if (contactCompanyCategory === 'typsa' && contactSelectedTypsaOrgUnitId) {
      return contacts.filter(c => c.typsa_org_unit_id === contactSelectedTypsaOrgUnitId);
    } else if (contactCompanyCategory && contactCompanyCategory !== 'typsa' && contactSelectedCompanyId) {
      return contacts.filter(c => c.company_id === contactSelectedCompanyId);
    }
    // If no filter selected, show all contacts
    return contacts;
  };

  // Handle linking voice note to company or TYPSA unit (for account_history)
  const handleLinkCompany = async () => {
    // Validate based on company link type
    if (companyLinkType === 'typsa') {
      if (!selectedLinkTypsaUnitId) return toast.error('Selecciona una unidad TYPSA');
    } else {
      if (!selectedLinkCompanyId) return toast.error('Selecciona una empresa');
    }
    
    setLinkingCompany(true);
    try {
      let url = `${API}/voice-notes/${id}/link-company`;
      if (companyLinkType === 'typsa') {
        url += `?typsa_org_unit_id=${selectedLinkTypsaUnitId}`;
      } else {
        url += `?company_id=${selectedLinkCompanyId}`;
      }
      
      await axios.post(url, null, {
        headers: { 'x-user-email': currentUser?.email }
      });
      toast.success(companyLinkType === 'typsa' ? 'Nota de voz vinculada a la unidad TYPSA' : 'Nota de voz vinculada a la empresa');
      setCompanyDialogOpen(false);
      await fetchNoteData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLinkingCompany(false);
    }
  };

  // Handle creating a new company and linking the voice note to it
  const handleCreateAndLinkCompany = async () => {
    // Validate required fields
    if (!newCompanyFormData.name.trim()) return toast.error('El nombre es obligatorio');
    if (!newCompanyFormData.company_type) return toast.error('El tipo de empresa es obligatorio');
    if (!newCompanyFormData.status) return toast.error('El estado es obligatorio');
    if (!newCompanyFormData.rol) return toast.error('El rol es obligatorio');
    
    setCreatingNewCompany(true);
    try {
      // Create the company
      const companyRes = await axios.post(`${API}/companies`, newCompanyFormData, {
        headers: { 'x-user-email': currentUser?.email }
      });
      
      const newCompanyId = companyRes.data.id;
      
      // Link the voice note to the new company
      await axios.post(`${API}/voice-notes/${id}/link-company?company_id=${newCompanyId}`, null, {
        headers: { 'x-user-email': currentUser?.email }
      });
      
      toast.success(`Empresa "${newCompanyFormData.name}" creada y vinculada`);
      setCompanyDialogOpen(false);
      
      // Reset form
      setNewCompanyFormData({ name: '', company_type: 'privada', sector: '', country: 'España', city: '', website: '', status: 'Prospecto', rol: 'Cliente' });
      setCompanyMode('existing');
      
      // Refresh data
      await fetchNoteData();
      await fetchRelatedData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCreatingNewCompany(false);
    }
  };

  // Get filtered TYPSA units for account_history linking
  const getFilteredTypsaUnitsForLinking = () => {
    if (companyLinkTypsaCategory) {
      const normalizedCategory = companyLinkTypsaCategory.toLowerCase().trim();
      return typsaOrgUnits.filter(u => 
        u.category && u.category.toLowerCase().trim() === normalizedCategory
      );
    }
    return [];
  };

  // Get filtered companies for account_history linking
  const getFilteredCompaniesForLinking = () => {
    if (companyFilterCategory && companyFilterCategory !== 'all') {
      return companies.filter(c => c.company_type === companyFilterCategory);
    }
    return companies;
  };

  const handleCreateCompany = async () => {
    if (!newCompanyData.name.trim() || !newCompanyData.status || !newCompanyData.rol) return toast.error('Completa los campos obligatorios');
    setCreatingCompany(true);
    try {
      const response = await axios.post(`${API}/companies`, { ...newCompanyData, company_type: editCompanyType === 'typsa' ? 'typsa' : newCompanyData.company_type });
      toast.success('Empresa creada');
      const companiesRes = await axios.get(`${API}/companies`);
      setCompanies(companiesRes.data);
      setEditFormData(prev => ({ ...prev, company_id: response.data.id, opportunity_id: '' }));
      setCreateCompanyMode(false);
    } catch (error) {
      toast.error('Error al crear empresa');
    } finally {
      setCreatingCompany(false);
    }
  };

  const handleCreateOpportunity = async () => {
    if (!newOpportunityData.title.trim()) return toast.error('El título es obligatorio');
    setCreatingOpportunity(true);
    try {
      const payload = {
        title: newOpportunityData.title,
        description: newOpportunityData.description || null,
        stage: newOpportunityData.stage || 'Detectada',
        estimated_value: newOpportunityData.estimated_value ? parseFloat(newOpportunityData.estimated_value) : null,
        company_id: editCompanyType !== 'typsa' ? editFormData.company_id : null,
        typsa_org_unit_id: editCompanyType === 'typsa' ? editFormData.typsa_org_unit_id : null
      };
      const response = await axios.post(`${API}/opportunities`, payload, { headers: { 'X-User-Email': currentUser?.email } });
      toast.success('Oportunidad creada');
      const oppsRes = await axios.get(`${API}/opportunities`);
      setOpportunities(oppsRes.data);
      setEditFormData(prev => ({ ...prev, opportunity_id: response.data.id }));
      setCreateOpportunityMode(false);
    } catch (error) {
      toast.error('Error al crear oportunidad: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCreatingOpportunity(false);
    }
  };

  // Helper functions
  const getCategoryBadge = (category) => {
    const opt = classificationOptions.find(o => o.id === category);
    return opt ? <Badge className={opt.color}>{opt.label}</Badge> : <Badge variant="secondary">Sin categoría</Badge>;
  };

  const updateTask = (index, field, value) => {
    // Feb 2026 fix: functional updater + deep-clone the target row so
    // back-to-back calls (e.g. assignee_email + owner from the same
    // onValueChange) never race on stale state, and the state array is not
    // shared with note.extracted_tasks_preview.
    setEditableTasks((prev) => {
      const next = prev.slice();
      const current = next[index] || {};
      next[index] = { ...current, [field]: value };
      return next;
    });
  };

  const removeTask = (index) => setEditableTasks(editableTasks.filter((_, i) => i !== index));

  // Loading/Error states
  if (loading) return <div className="p-4 md:p-8"><div className="text-sm text-muted-foreground">Cargando...</div></div>;
  if (!note) return <div className="p-4 md:p-8"><div className="text-sm text-muted-foreground">Nota de voz no encontrada</div></div>;

  return (
    <div className="p-4 md:p-8" data-testid="voice-note-detail-page">
      <Button variant="ghost" onClick={() => navigate('/voice-notes')} className="mb-4" data-testid="back-to-voice-notes-btn">
        <ArrowLeft className="w-4 h-4 mr-2" />Volver
      </Button>

      <div className="flex flex-col sm:flex-row items-start justify-between gap-4 mb-6 md:mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2">Nota de Voz</h1>
          <div className="text-sm text-muted-foreground">{new Date(note.date_time).toLocaleString('es-ES')}</div>
        </div>
        {isEditor && (
          <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)} data-testid="delete-voice-note-btn">
            <Trash2 className="w-4 h-4 mr-2" />Eliminar
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Audio Player */}
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Mic className="w-5 h-5" />Audio</CardTitle></CardHeader>
            <CardContent>{note.audio_url && <audio controls className="w-full" src={note.audio_url}>Tu navegador no soporta el elemento de audio.</audio>}</CardContent>
          </Card>

          {/* Category */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Categoría</CardTitle>
                {isEditor && (
                  <Button variant="outline" size="sm" onClick={() => { setNewCategory(note.classified_as || ''); setRecategorizeDialogOpen(true); }} data-testid="recategorize-btn">
                    <Pencil className="w-4 h-4 mr-2" />{note.classified_as ? 'Cambiar' : 'Asignar'}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                {getCategoryBadge(note.classified_as)}
                {hasAIProcessedData() && <span className="text-xs text-muted-foreground flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-500" />Procesado con IA</span>}
              </div>
              {note.classified_as && !hasAIProcessedData() && isEditor && (
                <Button variant="outline" size="sm" className="mt-3" onClick={handleProcessByCategory} disabled={processing}>
                  {processing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Procesando...</> : 'Procesar con IA'}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Transcript */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Transcripción</CardTitle>
                {!note.transcript_text && isEditor && (
                  <Button onClick={handleTranscribe} disabled={transcribing} data-testid="transcribe-btn">
                    {transcribing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Transcribiendo...</> : 'Transcribir'}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {note.transcript_text ? <p className="text-sm whitespace-pre-wrap">{note.transcript_text}</p> : <p className="text-sm text-muted-foreground">Haz clic en "Transcribir" para convertir el audio a texto.</p>}
            </CardContent>
          </Card>

          {/* Relational Analysis - For relationship_history */}
          {note.classified_as === 'relationship_history' && note.ai_structured_data && (
            <Card className="border-green-200 bg-green-50/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-800">
                  <User className="w-5 h-5" />
                  Análisis Relacional
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Context Summary */}
                {note.ai_structured_data.context_summary && (
                  <div className="p-3 bg-white rounded-lg border">
                    <p className="text-sm font-medium text-gray-700 mb-1">Resumen de Contexto</p>
                    <p className="text-sm text-gray-600">{note.ai_structured_data.context_summary}</p>
                  </div>
                )}
                
                {/* Relational Analysis Details */}
                {note.ai_structured_data.relational_analysis && (
                  <div className="grid gap-3">
                    {/* Perceived Profile */}
                    {note.ai_structured_data.relational_analysis.perceived_profile && (
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Perfil Percibido</p>
                        <p className="text-sm">{note.ai_structured_data.relational_analysis.perceived_profile}</p>
                      </div>
                    )}
                    
                    {/* Influence Level & Predisposition */}
                    <div className="grid sm:grid-cols-2 gap-3">
                      {note.ai_structured_data.relational_analysis.influence_level && note.ai_structured_data.relational_analysis.influence_level !== 'no detectado' && (
                        <div className="p-3 bg-white rounded-lg border">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Nivel de Influencia</p>
                          <Badge variant="outline" className={
                            note.ai_structured_data.relational_analysis.influence_level.toLowerCase().includes('alto') ? 'border-green-500 text-green-700' :
                            note.ai_structured_data.relational_analysis.influence_level.toLowerCase().includes('medio') ? 'border-yellow-500 text-yellow-700' :
                            'border-gray-500 text-gray-700'
                          }>
                            {note.ai_structured_data.relational_analysis.influence_level}
                          </Badge>
                        </div>
                      )}
                      
                      {note.ai_structured_data.relational_analysis.predisposition && note.ai_structured_data.relational_analysis.predisposition !== 'no detectado' && (
                        <div className="p-3 bg-white rounded-lg border">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Predisposición</p>
                          <Badge variant="outline" className={
                            note.ai_structured_data.relational_analysis.predisposition.toLowerCase().includes('favorable') ? 'border-green-500 text-green-700' :
                            note.ai_structured_data.relational_analysis.predisposition.toLowerCase().includes('neutral') ? 'border-yellow-500 text-yellow-700' :
                            'border-red-500 text-red-700'
                          }>
                            {note.ai_structured_data.relational_analysis.predisposition}
                          </Badge>
                        </div>
                      )}
                    </div>
                    
                    {/* Motivations */}
                    {note.ai_structured_data.relational_analysis.detected_motivations?.length > 0 && (
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Motivaciones Detectadas</p>
                        <ul className="list-disc list-inside space-y-1">
                          {note.ai_structured_data.relational_analysis.detected_motivations.map((m, i) => (
                            <li key={i} className="text-sm text-gray-600">{m}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Risks */}
                    {note.ai_structured_data.relational_analysis.relational_risks?.length > 0 && (
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">Riesgos Relacionales</p>
                        <ul className="list-disc list-inside space-y-1">
                          {note.ai_structured_data.relational_analysis.relational_risks.map((r, i) => (
                            <li key={i} className="text-sm text-red-600">{r}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Opportunities */}
                    {note.ai_structured_data.relational_analysis.strategic_opportunities?.length > 0 && (
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">Oportunidades Estratégicas</p>
                        <ul className="list-disc list-inside space-y-1">
                          {note.ai_structured_data.relational_analysis.strategic_opportunities.map((o, i) => (
                            <li key={i} className="text-sm text-blue-600">{o}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Strategic Account Analysis - For account_history */}
          {note.classified_as === 'account_history' && note.ai_structured_data && (
            <Card className="border-purple-200 bg-purple-50/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-800">
                  <Building2 className="w-5 h-5" />
                  Análisis Estratégico de Cuenta
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Executive Summary */}
                {note.ai_structured_data.executive_summary && (
                  <div className="p-3 bg-white rounded-lg border">
                    <p className="text-sm font-medium text-gray-700 mb-1">Resumen Ejecutivo</p>
                    <p className="text-sm text-gray-600">{note.ai_structured_data.executive_summary}</p>
                  </div>
                )}
                
                {/* Strategic Analysis Details */}
                {note.ai_structured_data.strategic_analysis && (
                  <div className="grid gap-3">
                    {/* Company Context */}
                    {note.ai_structured_data.strategic_analysis.company_context && (
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Contexto Estratégico</p>
                        <p className="text-sm">{note.ai_structured_data.strategic_analysis.company_context}</p>
                      </div>
                    )}
                    
                    {/* Relationship Positioning */}
                    {note.ai_structured_data.strategic_analysis.relationship_positioning && (
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Relación y Posicionamiento</p>
                        <p className="text-sm">{note.ai_structured_data.strategic_analysis.relationship_positioning}</p>
                      </div>
                    )}
                    
                    {/* Corporate Priorities */}
                    {note.ai_structured_data.strategic_analysis.corporate_priorities?.length > 0 && (
                      <div className="p-3 bg-white rounded-lg border">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Prioridades Corporativas</p>
                        <ul className="list-disc list-inside space-y-1">
                          {note.ai_structured_data.strategic_analysis.corporate_priorities.map((p, i) => (
                            <li key={i} className="text-sm text-gray-600">{p}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Financial Situation */}
                    {note.ai_structured_data.strategic_analysis.financial_situation && (
                      <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <p className="text-xs font-semibold text-yellow-700 uppercase tracking-wide mb-1">Situación Económica</p>
                        <p className="text-sm text-yellow-800">{note.ai_structured_data.strategic_analysis.financial_situation}</p>
                      </div>
                    )}
                    
                    {/* Structural Challenges */}
                    {note.ai_structured_data.strategic_analysis.structural_challenges?.length > 0 && (
                      <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                        <p className="text-xs font-semibold text-orange-700 uppercase tracking-wide mb-2">Retos Estructurales</p>
                        <ul className="list-disc list-inside space-y-1">
                          {note.ai_structured_data.strategic_analysis.structural_challenges.map((c, i) => (
                            <li key={i} className="text-sm text-orange-600">{c}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Risks & Sensitivities */}
                    {note.ai_structured_data.strategic_analysis.risks_sensitivities?.length > 0 && (
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-2">Riesgos y Sensibilidades</p>
                        <ul className="list-disc list-inside space-y-1">
                          {note.ai_structured_data.strategic_analysis.risks_sensitivities.map((r, i) => (
                            <li key={i} className="text-sm text-red-600">{r}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Future Opportunities */}
                    {note.ai_structured_data.strategic_analysis.future_opportunities?.length > 0 && (
                      <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">Oportunidades Futuras</p>
                        <ul className="list-disc list-inside space-y-1">
                          {note.ai_structured_data.strategic_analysis.future_opportunities.map((o, i) => (
                            <li key={i} className="text-sm text-green-600">{o}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Strategic Evaluation */}
                {note.ai_structured_data.strategic_evaluation && (
                  <div className="pt-3 mt-3 border-t">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Evaluación Estratégica</p>
                    <div className="flex flex-wrap gap-2">
                      {note.ai_structured_data.strategic_evaluation.priority_classification && (
                        <Badge variant="outline" className={`text-sm ${
                          note.ai_structured_data.strategic_evaluation.priority_classification === 'alta' ? 'border-green-500 text-green-700 bg-green-50' :
                          note.ai_structured_data.strategic_evaluation.priority_classification === 'media' ? 'border-yellow-500 text-yellow-700 bg-yellow-50' :
                          'border-gray-500 text-gray-700 bg-gray-50'
                        }`}>
                          Prioridad: {note.ai_structured_data.strategic_evaluation.priority_classification.toUpperCase()}
                        </Badge>
                      )}
                      {note.ai_structured_data.strategic_evaluation.modifies_perception && (
                        <Badge variant="outline" className="border-blue-500 text-blue-700 bg-blue-50 text-sm">
                          Modifica percepción
                        </Badge>
                      )}
                    </div>
                    {note.ai_structured_data.strategic_evaluation.classification_justification && (
                      <p className="text-xs text-muted-foreground mt-2 italic">
                        {note.ai_structured_data.strategic_evaluation.classification_justification}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Structured Summary */}
          {note.structured_summary && (
            <Card>
              <CardHeader><CardTitle>Resumen Estructurado</CardTitle></CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ 
                  __html: typeof note.structured_summary === 'string' 
                    ? note.structured_summary.replace(/\n/g, '<br/>').replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>') 
                    : JSON.stringify(note.structured_summary, null, 2).replace(/\n/g, '<br/>') 
                }} />
              </CardContent>
            </Card>
          )}

          {/* Extracted Tasks — outer wrapper only. VoiceNoteTasksEditor owns
              its own header and "Crear Tareas" button; the duplicate outer
              button was removed in Feb 2026. */}
          {note.extracted_tasks_preview && note.extracted_tasks_preview.length > 0 && (
            note.status === 'tasks_created' ? (
              <Card>
                <CardHeader><CardTitle>Tareas Extraídas</CardTitle></CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2 text-green-600"><CheckCircle className="w-5 h-5" /><span>Tareas creadas exitosamente</span></div>
                </CardContent>
              </Card>
            ) : (
              <VoiceNoteTasksEditor
                tasks={editableTasks}
                users={users}
                companies={companies}
                opportunities={opportunities}
                typsaOrgUnits={typsaOrgUnits}
                onUpdateTask={updateTask}
                onRemoveTask={removeTask}
                onCreateTasks={handleCreateTasks}
                creatingTasks={creatingTasks}
                isEditor={isEditor}
              />
            )
          )}

          {/* Link to Activity */}
          {!note.linked_activity_id && isEditor && note.classified_as !== 'relationship_history' && note.classified_as !== 'account_history' && (
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Link2 className="w-5 h-5" />Vincular a Actividad</CardTitle></CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">Esta nota de voz no está vinculada a ninguna actividad.</p>
                <Button onClick={() => { setActivityMode('new'); setActivityDialogOpen(true); }} data-testid="link-activity-btn">
                  <Plus className="w-4 h-4 mr-2" />Vincular a Actividad
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Link to Contact - For relationship_history */}
          {!note.linked_contact_id && isEditor && note.classified_as === 'relationship_history' && (
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><User className="w-5 h-5" />Vincular a Contacto</CardTitle></CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">Esta nota de historial de relación no está vinculada a ningún contacto.</p>
                <Button onClick={() => { 
                  setContactMode('existing'); 
                  setContactCompanyCategory('');
                  setContactTypsaOrgCategory('');
                  setContactSelectedCompanyId('');
                  setContactSelectedTypsaOrgUnitId('');
                  setSelectedContactId('');
                  setContactDialogOpen(true); 
                }} data-testid="link-contact-btn">
                  <Plus className="w-4 h-4 mr-2" />Vincular a Contacto
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Link to Company/TYPSA - For account_history */}
          {!note.linked_company_id && !note.linked_typsa_org_unit_id && isEditor && note.classified_as === 'account_history' && (
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Building2 className="w-5 h-5" />Vincular a Empresa o Unidad TYPSA</CardTitle></CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">Esta nota de historial de cuenta no está vinculada a ninguna empresa ni unidad TYPSA.</p>
                <Button onClick={() => { 
                  setCompanyMode('existing'); 
                  setCompanyLinkType('');
                  setCompanyLinkTypsaCategory('');
                  setSelectedLinkTypsaUnitId('');
                  setCompanyFilterCategory('');
                  setSelectedLinkCompanyId('');
                  setCompanyDialogOpen(true); 
                }} data-testid="link-company-btn">
                  <Plus className="w-4 h-4 mr-2" />Vincular a Empresa o TYPSA
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Progress tracker for activity type notes */}
          <VoiceNoteActivityProgress note={note} />
          
          {/* Info sidebar */}
          <VoiceNoteInfoSidebar note={note} linkedActivity={linkedActivity} linkedContact={linkedContact} linkedCompany={linkedCompany} linkedTypsaUnit={linkedTypsaUnit} />
        </div>
      </div>

      {/* Delete Dialog */}
      <DeleteDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen} title="Eliminar Nota de Voz" description="¿Estás seguro de que deseas eliminar esta nota de voz? Esta acción no se puede deshacer." onConfirm={handleDelete} />

      {/* Activity Link Dialog */}
      <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="activity-link-dialog">
          <DialogHeader><DialogTitle>Vincular a Actividad</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="py-4 space-y-4">
              <RadioGroup value={activityMode} onValueChange={setActivityMode}>
                <div className="flex items-center space-x-2"><RadioGroupItem value="new" id="new" /><Label htmlFor="new">Crear nueva actividad</Label></div>
                <div className="flex items-center space-x-2"><RadioGroupItem value="existing" id="existing" /><Label htmlFor="existing">Vincular a existente</Label></div>
              </RadioGroup>
              <Separator />
              {activityMode === 'new' ? (
                <div className="space-y-4">
                  {/* Title */}
                  <div><Label>Título *</Label><Input value={activityTitle} onChange={(e) => setActivityTitle(e.target.value)} placeholder="Título de la actividad" /></div>
                  
                  {/* Type */}
                  <div><Label>Tipo *</Label>
                    <Select value={activityType} onValueChange={setActivityType}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{activityTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select>
                  </div>
                  
                  {/* Date */}
                  <div><Label>Fecha *</Label>
                    <Input type="date" value={activityDate} onChange={(e) => setActivityDate(e.target.value)} />
                  </div>
                  
                  <Separator />
                  
                  {/* Company Type */}
                  <div><Label>Tipo de empresa *</Label>
                    <Select value={companyCategory} onValueChange={(v) => { setCompanyCategory(v); setTypsaOrgCategory(''); setSelectedCompanyId(''); setSelectedTypsaOrgUnitId(''); setSelectedOpportunityId(''); setShowNewCompanyForm(false); setShowNewOpportunityForm(false); }}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>{companyCategories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  
                  {/* TYPSA flow */}
                  {companyCategory === 'typsa' && (
                    <>
                      <div><Label>Categoría TYPSA *</Label>
                        <Select value={typsaOrgCategory} onValueChange={(v) => { setTypsaOrgCategory(v); setSelectedTypsaOrgUnitId(''); setSelectedOpportunityId(''); }}>
                          <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                          <SelectContent>{typsaOrgCategories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {typsaOrgCategory && (
                        <div><Label>Unidad TYPSA *</Label>
                          <Select value={selectedTypsaOrgUnitId || ''} onValueChange={(v) => { setSelectedTypsaOrgUnitId(v); setSelectedOpportunityId(''); }}>
                            <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                            <SelectContent>{filterTypsaUnitsByCategory(typsaOrgCategory).map(u => <SelectItem key={u.id} value={u.id}>{u.value}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Company flow (publica/privada) */}
                  {companyCategory && companyCategory !== 'typsa' && !showNewCompanyForm && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label>Empresa *</Label>
                        <Button variant="ghost" size="sm" onClick={() => setShowNewCompanyForm(true)}><Plus className="w-3 h-3 mr-1" />Nueva</Button>
                      </div>
                      <Select value={selectedCompanyId || ''} onValueChange={(v) => { setSelectedCompanyId(v); setSelectedOpportunityId(''); }}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent>{companies.filter(c => c.company_type === companyCategory).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* New Company Form */}
                  {showNewCompanyForm && (
                    <div className="border rounded-lg p-4 space-y-3 bg-muted/50">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">Nueva Empresa</span>
                        <Button variant="ghost" size="sm" onClick={() => setShowNewCompanyForm(false)}>Cancelar</Button>
                      </div>
                      <div><Label>Nombre *</Label><Input value={newActivityCompanyData.name} onChange={(e) => setNewActivityCompanyData(prev => ({ ...prev, name: e.target.value }))} /></div>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label>Estado *</Label>
                          <Select value={newActivityCompanyData.status} onValueChange={(v) => setNewActivityCompanyData(prev => ({ ...prev, status: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="Prospecto">Prospecto</SelectItem><SelectItem value="En relación">En relación</SelectItem><SelectItem value="Cliente">Cliente</SelectItem></SelectContent>
                          </Select>
                        </div>
                        <div><Label>Rol *</Label>
                          <Select value={newActivityCompanyData.rol} onValueChange={(v) => setNewActivityCompanyData(prev => ({ ...prev, rol: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="Cliente">Cliente</SelectItem><SelectItem value="Partner">Partner</SelectItem><SelectItem value="Competidor">Competidor</SelectItem></SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Button className="w-full" onClick={handleCreateCompanyInActivity} disabled={creatingCompany || !newActivityCompanyData.name.trim()}>
                        {creatingCompany ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creando...</> : 'Crear Empresa'}
                      </Button>
                    </div>
                  )}
                  
                  {/* Opportunity */}
                  {((companyCategory === 'typsa' && selectedTypsaOrgUnitId) || (companyCategory !== 'typsa' && selectedCompanyId)) && !showNewOpportunityForm && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label>Oportunidad *</Label>
                        <Button variant="ghost" size="sm" onClick={() => setShowNewOpportunityForm(true)}><Plus className="w-3 h-3 mr-1" />Nueva</Button>
                      </div>
                      <Select value={selectedOpportunityId || ''} onValueChange={(v) => v !== 'no-opportunities' && setSelectedOpportunityId(v)}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent>
                          {getFilteredOpportunitiesForActivity().length === 0 ? (
                            <SelectItem value="no-opportunities" disabled>No hay oportunidades - Crea una nueva</SelectItem>
                          ) : (
                            getFilteredOpportunitiesForActivity().map(o => <SelectItem key={o.id} value={o.id}>{o.title} ({o.stage})</SelectItem>)
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* New Opportunity Form */}
                  {showNewOpportunityForm && (
                    <div className="border rounded-lg p-4 space-y-3 bg-muted/50">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">Nueva Oportunidad</span>
                        <Button variant="ghost" size="sm" onClick={() => setShowNewOpportunityForm(false)}>Cancelar</Button>
                      </div>
                      <div><Label>Título *</Label><Input value={newActivityOpportunityData.title} onChange={(e) => setNewActivityOpportunityData(prev => ({ ...prev, title: e.target.value }))} /></div>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label>Etapa</Label>
                          <Select value={newActivityOpportunityData.stage} onValueChange={(v) => setNewActivityOpportunityData(prev => ({ ...prev, stage: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="Detectada">Detectada</SelectItem><SelectItem value="Calificada">Calificada</SelectItem><SelectItem value="Propuesta">Propuesta</SelectItem><SelectItem value="Presentada">Presentada</SelectItem></SelectContent>
                          </Select>
                        </div>
                        <div><Label>Valor estimado</Label><Input type="number" value={newActivityOpportunityData.estimated_value} onChange={(e) => setNewActivityOpportunityData(prev => ({ ...prev, estimated_value: e.target.value }))} placeholder="0" /></div>
                      </div>
                      <Button className="w-full" onClick={handleCreateOpportunityInActivity} disabled={creatingOpportunity || !newActivityOpportunityData.title.trim()}>
                        {creatingOpportunity ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creando...</> : 'Crear Oportunidad'}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Tipo de empresa */}
                  <div><Label>Tipo de empresa</Label>
                    <Select value={companyCategory} onValueChange={(v) => { setCompanyCategory(v); setTypsaOrgCategory(''); setSelectedCompanyId(''); setSelectedTypsaOrgUnitId(''); setSelectedActivityId(''); }}>
                      <SelectTrigger><SelectValue placeholder="Filtrar por tipo..." /></SelectTrigger>
                      <SelectContent><SelectItem value="all">Todas</SelectItem>{companyCategories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  
                  {/* Para TYPSA: Categoría de unidad */}
                  {companyCategory === 'typsa' && (
                    <div><Label>Categoría TYPSA</Label>
                      <Select value={typsaOrgCategory} onValueChange={(v) => { setTypsaOrgCategory(v); setSelectedTypsaOrgUnitId(''); setSelectedActivityId(''); }}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar categoría..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="division">División</SelectItem>
                          <SelectItem value="departamento">Departamento</SelectItem>
                          <SelectItem value="territorio">Territorio</SelectItem>
                          <SelectItem value="grupo">Grupo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Para TYPSA: Unidad específica */}
                  {companyCategory === 'typsa' && typsaOrgCategory && (
                    <div><Label>Unidad TYPSA</Label>
                      <Select value={selectedTypsaOrgUnitId || ''} onValueChange={(v) => { setSelectedTypsaOrgUnitId(v); setSelectedActivityId(''); }}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar unidad..." /></SelectTrigger>
                        <SelectContent>
                          {filterTypsaUnitsByCategory(typsaOrgCategory).length === 0 ? (
                            <SelectItem value="no-units" disabled>No hay unidades de este tipo</SelectItem>
                          ) : (
                            filterTypsaUnitsByCategory(typsaOrgCategory).map(u => <SelectItem key={u.id} value={u.id}>{u.value || u.name}</SelectItem>)
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Para Privada/Pública: Selector de empresa */}
                  {(companyCategory === 'privada' || companyCategory === 'publica') && (
                    <div><Label>Empresa</Label>
                      <Select value={selectedCompanyId || ''} onValueChange={(v) => { setSelectedCompanyId(v); setSelectedActivityId(''); }}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar empresa..." /></SelectTrigger>
                        <SelectContent>
                          {companies.filter(c => c.company_type === companyCategory).length === 0 ? (
                            <SelectItem value="no-companies" disabled>No hay empresas de este tipo</SelectItem>
                          ) : (
                            companies.filter(c => c.company_type === companyCategory).map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  {/* Selector de actividad - solo se muestra cuando se ha filtrado */}
                  {(companyCategory === 'all' || 
                    (companyCategory === 'typsa' && selectedTypsaOrgUnitId) || 
                    ((companyCategory === 'privada' || companyCategory === 'publica') && selectedCompanyId)) && (
                    <div><Label>Actividad sin documentar</Label>
                      <Select value={selectedActivityId || ''} onValueChange={(v) => v !== 'no-activities' && setSelectedActivityId(v)}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar actividad..." /></SelectTrigger>
                        <SelectContent>
                          {availableActivities.length === 0 ? (
                            <SelectItem value="no-activities" disabled>No hay actividades sin documentar</SelectItem>
                          ) : (
                            availableActivities.map(a => <SelectItem key={a.id} value={a.id}>{a.title} ({new Date(a.date).toLocaleDateString('es-ES')})</SelectItem>)
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActivityDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleLinkActivity} disabled={creatingActivity}>{creatingActivity ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Vinculando...</> : activityMode === 'new' ? 'Crear y Vincular' : 'Vincular'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Link Dialog - For relationship_history */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="contact-link-dialog">
          <DialogHeader><DialogTitle>Vincular a Contacto</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="py-4 space-y-4">
              <RadioGroup value={contactMode} onValueChange={setContactMode}>
                <div className="flex items-center space-x-2"><RadioGroupItem value="existing" id="contact-existing" /><Label htmlFor="contact-existing">Contacto existente</Label></div>
                <div className="flex items-center space-x-2"><RadioGroupItem value="new" id="contact-new" /><Label htmlFor="contact-new">Crear nuevo contacto</Label></div>
              </RadioGroup>
              <Separator />
              
              {contactMode === 'existing' ? (
                <div className="space-y-4">
                  {/* Company Type Filter */}
                  <div><Label>Filtrar por tipo de entidad</Label>
                    <Select value={contactCompanyCategory || 'all'} onValueChange={(v) => { 
                      setContactCompanyCategory(v === 'all' ? '' : v); 
                      setContactTypsaOrgCategory(''); 
                      setContactSelectedCompanyId(''); 
                      setContactSelectedTypsaOrgUnitId(''); 
                      setSelectedContactId(''); 
                    }}>
                      <SelectTrigger><SelectValue placeholder="Todos los contactos" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos los contactos</SelectItem>
                        {companyCategories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* TYPSA Category */}
                  {contactCompanyCategory === 'typsa' && (
                    <div><Label>Categoría TYPSA</Label>
                      <Select value={contactTypsaOrgCategory || ''} onValueChange={(v) => { 
                        setContactTypsaOrgCategory(v); 
                        setContactSelectedTypsaOrgUnitId(''); 
                        setSelectedContactId(''); 
                      }}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent>{typsaOrgCategories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* TYPSA Unit */}
                  {contactCompanyCategory === 'typsa' && contactTypsaOrgCategory && (
                    <div><Label>Unidad TYPSA</Label>
                      <Select value={contactSelectedTypsaOrgUnitId || ''} onValueChange={(v) => { 
                        setContactSelectedTypsaOrgUnitId(v); 
                        setSelectedContactId(''); 
                      }}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent>
                          {filterTypsaUnitsByCategory(contactTypsaOrgCategory).map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.value}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Company (for non-TYPSA) */}
                  {contactCompanyCategory && contactCompanyCategory !== 'typsa' && (
                    <div><Label>Empresa</Label>
                      <Select value={contactSelectedCompanyId || ''} onValueChange={(v) => { 
                        setContactSelectedCompanyId(v); 
                        setSelectedContactId(''); 
                      }}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent>
                          {companies.filter(c => c.company_type === contactCompanyCategory).map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Contact Selection */}
                  <div><Label>Contacto *</Label>
                    <Select value={selectedContactId || ''} onValueChange={setSelectedContactId}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar contacto..." /></SelectTrigger>
                      <SelectContent>
                        {getFilteredContacts().length === 0 ? (
                          <SelectItem value="no-contacts" disabled>No hay contactos disponibles</SelectItem>
                        ) : (
                          getFilteredContacts().map(c => {
                            const name = `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email;
                            return <SelectItem key={c.id} value={c.id}>{name} {c.position && `(${c.position})`}</SelectItem>;
                          })
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                /* Create New Contact Form */
                <div className="space-y-4">
                  {/* Company/TYPSA Selection for new contact */}
                  <div><Label>Tipo de entidad *</Label>
                    <Select value={contactCompanyCategory || ''} onValueChange={(v) => { 
                      setContactCompanyCategory(v); 
                      setContactTypsaOrgCategory(''); 
                      setContactSelectedCompanyId(''); 
                      setContactSelectedTypsaOrgUnitId(''); 
                    }}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar tipo..." /></SelectTrigger>
                      <SelectContent>
                        {companyCategories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* TYPSA Category */}
                  {contactCompanyCategory === 'typsa' && (
                    <div><Label>Categoría TYPSA *</Label>
                      <Select value={contactTypsaOrgCategory || ''} onValueChange={(v) => { 
                        setContactTypsaOrgCategory(v); 
                        setContactSelectedTypsaOrgUnitId(''); 
                      }}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent>{typsaOrgCategories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* TYPSA Unit */}
                  {contactCompanyCategory === 'typsa' && contactTypsaOrgCategory && (
                    <div><Label>Unidad TYPSA *</Label>
                      <Select value={contactSelectedTypsaOrgUnitId || ''} onValueChange={setContactSelectedTypsaOrgUnitId}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent>
                          {filterTypsaUnitsByCategory(contactTypsaOrgCategory).map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.value}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Company (for non-TYPSA) */}
                  {contactCompanyCategory && contactCompanyCategory !== 'typsa' && (
                    <div><Label>Empresa *</Label>
                      <Select value={contactSelectedCompanyId || ''} onValueChange={setContactSelectedCompanyId}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent>
                          {companies.filter(c => c.company_type === contactCompanyCategory).map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <Separator />
                  
                  {/* Contact Personal Data */}
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Nombre *</Label>
                      <Input 
                        value={newContactData.first_name} 
                        onChange={(e) => setNewContactData(prev => ({ ...prev, first_name: e.target.value }))}
                        placeholder="Juan"
                      />
                    </div>
                    <div><Label>Apellido *</Label>
                      <Input 
                        value={newContactData.last_name} 
                        onChange={(e) => setNewContactData(prev => ({ ...prev, last_name: e.target.value }))}
                        placeholder="García"
                      />
                    </div>
                  </div>
                  
                  <div><Label>Email *</Label>
                    <Input 
                      type="email"
                      value={newContactData.email} 
                      onChange={(e) => setNewContactData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="juan@empresa.com"
                    />
                  </div>
                  
                  <div><Label>Teléfono</Label>
                    <Input 
                      value={newContactData.phone} 
                      onChange={(e) => setNewContactData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+34 600 123 456"
                    />
                  </div>
                  
                  <div><Label>Rol / Cargo</Label>
                    <Input 
                      value={newContactData.role} 
                      onChange={(e) => setNewContactData(prev => ({ ...prev, role: e.target.value }))}
                      placeholder="Director Comercial"
                    />
                  </div>
                  
                  <div><Label>LinkedIn</Label>
                    <Input 
                      value={newContactData.linkedin} 
                      onChange={(e) => setNewContactData(prev => ({ ...prev, linkedin: e.target.value }))}
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactDialogOpen(false)}>Cancelar</Button>
            {contactMode === 'existing' ? (
              <Button onClick={handleLinkContact} disabled={linkingContact || !selectedContactId}>
                {linkingContact ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Vinculando...</> : 'Vincular a Contacto'}
              </Button>
            ) : (
              <Button onClick={handleCreateAndLinkContact} disabled={creatingNewContact}>
                {creatingNewContact ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creando...</> : 'Crear y Vincular'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Company/TYPSA Link Dialog - For account_history */}
      <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="company-link-dialog">
          <DialogHeader><DialogTitle>Vincular a Empresa o Unidad TYPSA</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="py-4 space-y-4">
              <RadioGroup value={companyMode} onValueChange={(v) => {
                setCompanyMode(v);
                if (v === 'new') {
                  setCompanyLinkType('');
                }
              }}>
                <div className="flex items-center space-x-2"><RadioGroupItem value="existing" id="company-existing" /><Label htmlFor="company-existing">Entidad existente</Label></div>
                <div className="flex items-center space-x-2"><RadioGroupItem value="new" id="company-new" /><Label htmlFor="company-new">Crear nueva empresa</Label></div>
              </RadioGroup>
              <Separator />
              
              {companyMode === 'existing' ? (
                <div className="space-y-4">
                  {/* Entity Type Selection - TYPSA, Privada, Pública */}
                  <div><Label>Tipo de entidad *</Label>
                    <Select value={companyLinkType || ''} onValueChange={(v) => { 
                      setCompanyLinkType(v);
                      setCompanyLinkTypsaCategory('');
                      setSelectedLinkTypsaUnitId('');
                      setCompanyFilterCategory('');
                      setSelectedLinkCompanyId(''); 
                    }}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar tipo..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="typsa">TYPSA</SelectItem>
                        <SelectItem value="privada">Empresa Privada</SelectItem>
                        <SelectItem value="publica">Empresa Pública</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* TYPSA Flow - Category then Unit */}
                  {companyLinkType === 'typsa' && (
                    <>
                      <div><Label>Categoría TYPSA *</Label>
                        <Select value={companyLinkTypsaCategory || ''} onValueChange={(v) => { 
                          setCompanyLinkTypsaCategory(v); 
                          setSelectedLinkTypsaUnitId(''); 
                        }}>
                          <SelectTrigger><SelectValue placeholder="Seleccionar categoría..." /></SelectTrigger>
                          <SelectContent>
                            {typsaOrgCategories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {companyLinkTypsaCategory && (
                        <div><Label>Unidad TYPSA *</Label>
                          <Select value={selectedLinkTypsaUnitId || ''} onValueChange={setSelectedLinkTypsaUnitId}>
                            <SelectTrigger><SelectValue placeholder="Seleccionar unidad..." /></SelectTrigger>
                            <SelectContent>
                              {getFilteredTypsaUnitsForLinking().length === 0 ? (
                                <SelectItem value="no-units" disabled>No hay unidades disponibles</SelectItem>
                              ) : (
                                getFilteredTypsaUnitsForLinking().map(u => (
                                  <SelectItem key={u.id} value={u.id}>{u.name || u.value}</SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </>
                  )}

                  {/* Company Flow - Privada or Pública */}
                  {(companyLinkType === 'privada' || companyLinkType === 'publica') && (
                    <div><Label>Empresa *</Label>
                      <Select value={selectedLinkCompanyId || ''} onValueChange={setSelectedLinkCompanyId}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar empresa..." /></SelectTrigger>
                        <SelectContent>
                          {companies.filter(c => c.company_type === companyLinkType).length === 0 ? (
                            <SelectItem value="no-companies" disabled>No hay empresas disponibles</SelectItem>
                          ) : (
                            companies.filter(c => c.company_type === companyLinkType).map(c => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              ) : (
                /* Create New Company Form */
                <div className="space-y-4">
                  <div><Label>Nombre de la empresa *</Label>
                    <Input 
                      value={newCompanyFormData.name} 
                      onChange={(e) => setNewCompanyFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Nombre de la empresa"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Tipo *</Label>
                      <Select value={newCompanyFormData.company_type} onValueChange={(v) => setNewCompanyFormData(prev => ({ ...prev, company_type: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="privada">Privada</SelectItem>
                          <SelectItem value="publica">Pública</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Sector</Label>
                      <Input 
                        value={newCompanyFormData.sector} 
                        onChange={(e) => setNewCompanyFormData(prev => ({ ...prev, sector: e.target.value }))}
                        placeholder="Ej: Tecnología"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>País</Label>
                      <Input 
                        value={newCompanyFormData.country} 
                        onChange={(e) => setNewCompanyFormData(prev => ({ ...prev, country: e.target.value }))}
                        placeholder="España"
                      />
                    </div>
                    <div><Label>Ciudad</Label>
                      <Input 
                        value={newCompanyFormData.city} 
                        onChange={(e) => setNewCompanyFormData(prev => ({ ...prev, city: e.target.value }))}
                        placeholder="Madrid"
                      />
                    </div>
                  </div>
                  
                  <div><Label>Sitio web</Label>
                    <Input 
                      value={newCompanyFormData.website} 
                      onChange={(e) => setNewCompanyFormData(prev => ({ ...prev, website: e.target.value }))}
                      placeholder="https://..."
                    />
                  </div>
                  
                  <Separator />
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Estado *</Label>
                      <Select value={newCompanyFormData.status} onValueChange={(v) => setNewCompanyFormData(prev => ({ ...prev, status: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Prospecto">Prospecto</SelectItem>
                          <SelectItem value="Lead">Lead</SelectItem>
                          <SelectItem value="Activo">Activo</SelectItem>
                          <SelectItem value="Inactivo">Inactivo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Rol *</Label>
                      <Select value={newCompanyFormData.rol} onValueChange={(v) => setNewCompanyFormData(prev => ({ ...prev, rol: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Cliente">Cliente</SelectItem>
                          <SelectItem value="Partner">Partner</SelectItem>
                          <SelectItem value="Proveedor">Proveedor</SelectItem>
                          <SelectItem value="Competidor">Competidor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompanyDialogOpen(false)}>Cancelar</Button>
            {companyMode === 'existing' ? (
              <Button 
                onClick={handleLinkCompany} 
                disabled={linkingCompany || (companyLinkType === 'typsa' ? !selectedLinkTypsaUnitId : !selectedLinkCompanyId)}
              >
                {linkingCompany ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Vinculando...</> : companyLinkType === 'typsa' ? 'Vincular a Unidad TYPSA' : 'Vincular a Empresa'}
              </Button>
            ) : (
              <Button onClick={handleCreateAndLinkCompany} disabled={creatingNewCompany}>
                {creatingNewCompany ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creando...</> : 'Crear y Vincular'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-voice-note-dialog">
          <DialogHeader><DialogTitle>Editar Nota de Voz</DialogTitle></DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-4 py-4">
              <div><Label>Creador</Label><Input value={editFormData.creator} onChange={(e) => setEditFormData(prev => ({ ...prev, creator: e.target.value }))} /></div>
              <Separator />
              <div><Label>Tipo de Empresa</Label>
                <Select value={editCompanyType || 'none'} onValueChange={(v) => { setEditCompanyType(v === 'none' ? '' : v); setEditTypsaCategory(''); setEditFormData(prev => ({ ...prev, company_id: '', typsa_org_unit_id: '', opportunity_id: '' })); setCreateCompanyMode(false); setCreateOpportunityMode(false); }}>
                  <SelectTrigger data-testid="edit-company-type-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Ninguna</SelectItem>{companyCategories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {editCompanyType === 'typsa' && (
                <>
                  <div><Label>Categoría TYPSA</Label>
                    <Select value={editTypsaCategory || 'none'} onValueChange={(v) => { setEditTypsaCategory(v === 'none' ? '' : v); setEditFormData(prev => ({ ...prev, typsa_org_unit_id: '', opportunity_id: '' })); }}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Seleccionar...</SelectItem>{typsaOrgCategories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {editTypsaCategory && (
                    <div><Label>Unidad TYPSA</Label>
                      <Select value={editFormData.typsa_org_unit_id || 'none'} onValueChange={(v) => setEditFormData(prev => ({ ...prev, typsa_org_unit_id: v === 'none' ? '' : v, company_id: '', opportunity_id: '' }))}>
                        <SelectTrigger data-testid="edit-typsa-unit-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                        <SelectContent><SelectItem value="none">Ninguna</SelectItem>{getFilteredTypsaUnits().map(u => <SelectItem key={u.id} value={u.id}>{u.value}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              )}
              {editCompanyType && editCompanyType !== 'typsa' && !createCompanyMode && (
                <div>
                  <div className="flex items-center justify-between"><Label>Empresa</Label><Button variant="ghost" size="sm" onClick={() => setCreateCompanyMode(true)}><Plus className="w-4 h-4 mr-1" />Nueva</Button></div>
                  <Select value={editFormData.company_id || 'none'} onValueChange={(v) => { setEditFormData(prev => ({ ...prev, company_id: v === 'none' ? '' : v, opportunity_id: '' })); setCreateOpportunityMode(false); }}>
                    <SelectTrigger data-testid="edit-company-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Ninguna</SelectItem>{getFilteredCompanies().map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {createCompanyMode && (
                <div className="border rounded-lg p-4 space-y-3 bg-muted/50">
                  <div className="flex items-center justify-between"><span className="font-medium text-sm">Nueva Empresa</span><Button variant="ghost" size="sm" onClick={() => setCreateCompanyMode(false)}>Cancelar</Button></div>
                  <div><Label>Nombre *</Label><Input value={newCompanyData.name} onChange={(e) => setNewCompanyData(prev => ({ ...prev, name: e.target.value }))} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Estado *</Label>
                      <Select value={newCompanyData.status} onValueChange={(v) => setNewCompanyData(prev => ({ ...prev, status: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="Prospecto">Prospecto</SelectItem><SelectItem value="En relación">En relación</SelectItem><SelectItem value="Cliente">Cliente</SelectItem><SelectItem value="No estratégica">No estratégica</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div><Label>Rol *</Label>
                      <Select value={newCompanyData.rol} onValueChange={(v) => setNewCompanyData(prev => ({ ...prev, rol: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="Cliente">Cliente</SelectItem><SelectItem value="Partner">Partner</SelectItem><SelectItem value="Competidor">Competidor</SelectItem><SelectItem value="Mixto">Mixto</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button className="w-full" onClick={handleCreateCompany} disabled={creatingCompany || !newCompanyData.name.trim() || !newCompanyData.status || !newCompanyData.rol}>
                    {creatingCompany ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creando...</> : 'Crear Empresa'}
                  </Button>
                </div>
              )}
              {(editFormData.company_id || editFormData.typsa_org_unit_id) && !createOpportunityMode && (
                <div>
                  <div className="flex items-center justify-between"><Label>Oportunidad</Label><Button variant="ghost" size="sm" onClick={() => setCreateOpportunityMode(true)}><Plus className="w-4 h-4 mr-1" />Nueva</Button></div>
                  <Select value={editFormData.opportunity_id || 'none'} onValueChange={(v) => setEditFormData(prev => ({ ...prev, opportunity_id: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="edit-opportunity-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                    <SelectContent><SelectItem value="none">Ninguna</SelectItem>{getFilteredOpportunities().map(o => <SelectItem key={o.id} value={o.id}>{o.title || o.name} ({o.stage})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {createOpportunityMode && (
                <div className="border rounded-lg p-4 space-y-3 bg-muted/50">
                  <div className="flex items-center justify-between"><span className="font-medium text-sm">Nueva Oportunidad</span><Button variant="ghost" size="sm" onClick={() => setCreateOpportunityMode(false)}>Cancelar</Button></div>
                  <div><Label>Título *</Label><Input value={newOpportunityData.title} onChange={(e) => setNewOpportunityData(prev => ({ ...prev, title: e.target.value }))} /></div>
                  <div><Label>Descripción</Label><Textarea value={newOpportunityData.description} onChange={(e) => setNewOpportunityData(prev => ({ ...prev, description: e.target.value }))} rows={2} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Etapa</Label>
                      <Select value={newOpportunityData.stage} onValueChange={(v) => setNewOpportunityData(prev => ({ ...prev, stage: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="Detectada">Detectada</SelectItem><SelectItem value="Calificada">Calificada</SelectItem><SelectItem value="Propuesta">Propuesta</SelectItem><SelectItem value="Presentada">Presentada</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div><Label>Valor Estimado</Label><Input type="number" value={newOpportunityData.estimated_value} onChange={(e) => setNewOpportunityData(prev => ({ ...prev, estimated_value: e.target.value }))} placeholder="0.00" /></div>
                  </div>
                  <Button className="w-full" onClick={handleCreateOpportunity} disabled={creatingOpportunity || !newOpportunityData.title.trim()}>
                    {creatingOpportunity ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creando...</> : 'Crear Oportunidad'}
                  </Button>
                </div>
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">{saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</> : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recategorize Dialog */}
      <Dialog open={recategorizeDialogOpen} onOpenChange={setRecategorizeDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="recategorize-dialog">
          <DialogHeader><DialogTitle>Cambiar Categoría</DialogTitle></DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">Selecciona la nueva categoría para esta nota de voz. El sistema reprocesará el audio con IA según la categoría elegida.</p>
            <RadioGroup value={newCategory} onValueChange={setNewCategory} className="space-y-3">
              {classificationOptions.map((opt) => (
                <div key={opt.id} className="flex items-center space-x-3">
                  <RadioGroupItem value={opt.id} id={`cat-${opt.id}`} />
                  <Label htmlFor={`cat-${opt.id}`} className="flex-1 cursor-pointer"><Badge className={opt.color}>{opt.label}</Badge></Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecategorizeDialogOpen(false)} disabled={recategorizing}>Cancelar</Button>
            <Button onClick={handleRecategorize} disabled={recategorizing || !newCategory || newCategory === note?.classified_as} data-testid="confirm-recategorize-btn">
              {recategorizing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Procesando...</> : 'Cambiar y Reprocesar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VoiceNoteDetail;
